import React, { useState, createContext, useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import GlobalStyles from './styles/GlobalStyles';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import GoogleAnalytics from './components/GoogleAnalytics';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiesPage from './pages/CookiesPage';
import SitemapPage from './pages/SitemapPage';
import ContactPage from './pages/ContactPage';
import BlogListPage from './pages/BlogListPage';
import BlogPage from './pages/BlogPage';
import VerticallyIntegratedQSRPage from './pages/blog/VerticallyIntegratedQSRPage';
import ThirdPartyDeliveryIntegrationPage from './pages/blog/ThirdPartyDeliveryIntegrationPage';
import BusinessPortalsPage from './pages/solutions/BusinessPortalsPage';
import SystemIntegrationsPage from './pages/solutions/SystemIntegrationsPage';
import WorkflowAutomationPage from './pages/solutions/WorkflowAutomationPage';
import ExternalRedirect from './components/ExternalRedirect';
import JoinTeamModal from './components/JoinTeamModal';
import LearnMoreModal from './components/LearnMoreModal';
import { SupabaseProvider } from './contexts/SupabaseContext';

// Create context for modal state
interface ModalContextType {
  openJoinTeamModal: () => void;
  openLearnMoreModal: (serviceName?: string, title?: string) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

// Hook to use the modal context
export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
};

// Extend the Window interface to include our custom property
declare global {
  interface Window {
    __UPTREND_APP?: {
      openJoinTeamModal: () => void;
    };
  }
}

// Component to conditionally render header/footer based on route
const AppLayout: React.FC = () => {
  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/solutions/business-portals" element={<BusinessPortalsPage />} />
        <Route path="/solutions/system-integrations" element={<SystemIntegrationsPage />} />
        <Route path="/solutions/workflow-automation" element={<WorkflowAutomationPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/vertically-integrated-qsr-order-processing" element={<VerticallyIntegratedQSRPage />} />
        <Route path="/blog/third-party-delivery-integration-real-cost" element={<ThirdPartyDeliveryIntegrationPage />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
        <Route path="/documentation" element={<ExternalRedirect to="https://docs.uptrendsystems.com" />} />
        <Route path="/documentation/kds" element={<ExternalRedirect to="https://docs.uptrendsystems.com/kds/intro" />} />
        <Route path="/documentation/oms" element={<ExternalRedirect to="https://docs.uptrendsystems.com/oms/intro" />} />
        <Route path="/documentation/web" element={<ExternalRedirect to="https://docs.uptrendsystems.com" />} />
        <Route path="/documentation/pos" element={<ExternalRedirect to="https://docs.uptrendsystems.com/pos/intro" />} />
        {/* Add more routes here as your application grows */}
      </Routes>
      <Footer />
    </>
  );
};

const App: React.FC = () => {
  const [showJoinTeamModal, setShowJoinTeamModal] = useState(false);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const [currentService, setCurrentService] = useState<string | undefined>(undefined);
  const [currentModalTitle, setCurrentModalTitle] = useState<string | undefined>(undefined);
  
  const openJoinTeamModal = () => setShowJoinTeamModal(true);
  const closeJoinTeamModal = () => setShowJoinTeamModal(false);
  
  const openLearnMoreModal = (serviceName?: string, title?: string) => {
    setCurrentService(serviceName);
    setCurrentModalTitle(title);
    setShowLearnMoreModal(true);
  };
  const closeLearnMoreModal = () => setShowLearnMoreModal(false);
  
  // Value to be provided to context consumers
  const modalContextValue = {
    openJoinTeamModal,
    openLearnMoreModal,
  };
  
  // Expose modal functions to window for Easter egg functionality
  useEffect(() => {
    window.__UPTREND_APP = {
      openJoinTeamModal,
    };
    
    // Check URL params on load for direct modal trigger
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openJoinModal') === 'true') {
      openJoinTeamModal();
    }
    
    return () => {
      delete window.__UPTREND_APP;
    };
  }, []);
  
  return (
    <HelmetProvider>
      <SupabaseProvider>
        <Router>
          <ScrollToTop />
          <GoogleAnalytics />
          <GlobalStyles />
          <ModalContext.Provider value={modalContextValue}>
            {/* Make modals accessible throughout the app */}
            <JoinTeamModal isOpen={showJoinTeamModal} onClose={closeJoinTeamModal} />
            <LearnMoreModal 
              isOpen={showLearnMoreModal} 
              onClose={closeLearnMoreModal} 
              serviceName={currentService}
              title={currentModalTitle}
            />
            
            <AppLayout />
          </ModalContext.Provider>
        </Router>
      </SupabaseProvider>
    </HelmetProvider>
  );
};

export default App;
